/**
 * The admin API this page talks to (apps/api/src/admin/routes.ts).
 *
 * Sign-in, the session and sign-out are not here: they are the shared
 * `createAuthClient` from @acquire-intelligence/ai-labs-auth-component, the
 * same client every project uses, re-exported below as `authClient`.
 *
 * Same-origin relative paths, always: behind CloudFront /api routes to the
 * Lambda function and everything else is this bundle, and in development the
 * Vite proxy reproduces that. So no CORS and no configured API base URL.
 */
import {
  type AuthSessionUser,
  createAuthClient,
} from "@acquire-intelligence/ai-labs-auth-component";

export const authClient = createAuthClient();

export type AuthenticatedUser = AuthSessionUser;

/**
 * An error carrying the API's own code, so the form can decide what to say
 * without matching on prose. The codes are the ones in
 * apps/api/src/admin/routes.ts.
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function readError(response: Response): Promise<ApiError> {
  // A 502 from CloudFront or a Lambda that failed before the app ran answers HTML,
  // not JSON. Parsing has to be allowed to fail without replacing the real
  // status with a parser error.
  let code = "unexpected_error";
  let message = "Something went wrong. Try again.";

  try {
    const body = (await response.json()) as { error?: string; message?: string };
    if (body.error) code = body.error;
    if (body.message) message = body.message;
  } catch {
    // Keep the defaults.
  }

  return new ApiError(code, message, response.status);
}

/**
 * The admin API (apps/api/src/admin/routes.ts). Every call here needs a
 * session carrying the `admin` group; a session without it gets a 403 an
 * admin-dashboard caller turns into readError like any other refusal.
 */

export type Project = {
  slug: string;
  name: string;
};

export async function fetchProjects(): Promise<Project[]> {
  const response = await fetch("/api/admin/projects", {
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw await readError(response);
  const body = (await response.json()) as { projects: Project[] };
  return body.projects;
}

export async function createProject(input: Project): Promise<Project> {
  const response = await fetch("/api/admin/projects", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as Project;
}

export async function deleteProject(slug: string): Promise<void> {
  const response = await fetch(`/api/admin/projects/${encodeURIComponent(slug)}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw await readError(response);
}

/** The standard group set (docs/ARCHITECTURE.md). admin and superuser reach
 * every project; user and guests reach nothing until granted. */
export const STANDARD_GROUPS = ["admin", "superuser", "user", "guests"] as const;
export type StandardGroup = (typeof STANDARD_GROUPS)[number];

export type AdminUserSummary = {
  userId: string;
  email: string;
  name: string | null;
  enabled: boolean;
  status: string;
  groups: string[];
};

export async function fetchUsers(): Promise<AdminUserSummary[]> {
  const response = await fetch("/api/admin/users", {
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw await readError(response);
  const body = (await response.json()) as { users: AdminUserSummary[] };
  return body.users;
}

export async function createAdminUser(input: {
  email: string;
  name: string | null;
  groups: string[];
}): Promise<AdminUserSummary> {
  const response = await fetch("/api/admin/users", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as AdminUserSummary;
}

export async function setUserGroups(userId: string, groups: string[]): Promise<string[]> {
  const response = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/groups`, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ groups }),
  });
  if (!response.ok) throw await readError(response);
  const body = (await response.json()) as { groups: string[] };
  return body.groups;
}

export async function fetchUserGrants(userId: string): Promise<string[]> {
  const response = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/grants`, {
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw await readError(response);
  const body = (await response.json()) as { projects: string[] };
  return body.projects;
}

export async function setUserGrants(userId: string, projects: string[]): Promise<string[]> {
  const response = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/grants`, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ projects }),
  });
  if (!response.ok) throw await readError(response);
  const body = (await response.json()) as { projects: string[] };
  return body.projects;
}
